import { useEffect, useMemo, useRef, useState } from "react";
import { MdOutlineMonitorHeart } from "react-icons/md";
import { useLocation } from "react-router-dom";

import LeftGraph from "../../components/Graph/LeftGraph";
import Header from "../../components/Header/Header";
import ListItem from "../../components/ListItem/ListItem";
import SidebarFooter from "../../components/SidebarFooter/SidebarFooter";
import SiderbarHeading from "../../components/SiderbarHeading/SiderbarHeading";
import LambdaTable from "../../components/table/LambdaTable";

import { LAMBDAS } from "../../data/LambdaData";

const coblEnv = process.env.REACT_APP_FLAG_COBL;
const devDemoEnv = process.env.REACT_APP_FLAG_DEV_DEMO
const sandevsanprodEnv = process.env.REACT_APP_FLAG_SANDEV_SANPROD
const hcInternalEnv = process.env.REACT_APP_FLAG_HCINTERNAL

/* ================= ENV CONFIG ================= */
const ENV_CONFIG = {
  "/dev-env": {
    env: "HCS-DEV",
    ws: "wss://8ig50wx1t9.execute-api.ap-southeast-2.amazonaws.com/acs_dev",
    rest:
      "https://d5ojqd4q07.execute-api.ap-southeast-2.amazonaws.com/acs_dev/acs_Live_api_monitoring",
  },
  "/test-env": {
    env: "Pallasai_Test",
    ws: "wss://raax9mjpm3.execute-api.ap-southeast-2.amazonaws.com/Pallasai_Test",
    rest:
      "https://61yhl2k4b4.execute-api.ap-southeast-2.amazonaws.com/acs_test/acs_Live_api_monitoring",
  },
  "/san-env": {
    env: "HCS-iMedx_SAN",
    ws: "wss://qs5fkezi08.execute-api.ap-southeast-2.amazonaws.com/acs_iMedx_san",
    rest: "https://2n5jxf0wm7.execute-api.ap-southeast-2.amazonaws.com/acs_iMedx_SAN/acs_Live_api_monitoring",
  },
  "/san-prod-env": {
    env: "HCS-imedx-san-prod",
    ws: "wss://e861o0eu28.execute-api.ap-southeast-2.amazonaws.com/acs_imedx_san_prod",
    rest: "https://ybwm9ujgtb.execute-api.ap-southeast-2.amazonaws.com/acs_imedx_san_prod/acs_Live_api_monitoring",
  },
  "/demo-env": {
    env: "Internal_ENV(DEMO)",
    ws: "wss://0a695vjmr4.execute-api.ap-southeast-2.amazonaws.com/acs_demo",
    rest: "https://54oqoka0bi.execute-api.ap-southeast-2.amazonaws.com/hcs_internal/acs_Live_api_monitoring",
  },
  "/cobl-env": {
    env: "COBLH_ENV",
    ws: "wss://vr2kqq1p4k.execute-api.ap-southeast-2.amazonaws.com/acs_coblh_dev",
    rest: "https://88eybpbqh4.execute-api.ap-southeast-2.amazonaws.com/acs_coblh_dev/acs_Live_api_monitoring_dev",
  },
  "/hcinternal-env": {
    env: "HCS-Internal(san-hc)",
    ws: "wss://k3dkzq4km6.execute-api.ap-southeast-2.amazonaws.com/acs-internal",
    rest: "https://xpsgv84w87.execute-api.ap-southeast-2.amazonaws.com/acs_san_hc/acs_Live_api_monitoring",
  },
};

/* ================= DEFAULT STATE ================= */
const createDefaultLambdaStatus = () =>
  LAMBDAS.reduce((acc, fn) => {
    acc[fn] = { status: "🟢", date: "", time: "" };
    return acc;
  }, {});

/* ================= COMPONENT ================= */
const LambdaDashBoard = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const envConfig = ENV_CONFIG[currentPath];

  const wsRef = useRef(null);

  // 🔐 STATUS STORED PER ENV
  const [statusByEnv, setStatusByEnv] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isDark, setIsDark] = useState(true);
  const [isConnected, setIsConnected] = useState(false);

  /* ================= LOAD FROM DYNAMODB ================= */
  const loadFromDynamoDB = async () => {
    if (!envConfig) return;

    try {
      const res = await fetch(`${envConfig.rest}?env=${envConfig.env}`);
      const data = await res.json();

      const hydrated = createDefaultLambdaStatus();

      data.forEach((item) => {
        hydrated[item.lambda] = {
          status: item.color === "red" ? "🔴" : "🟢",
          date: item.lastUpdatedAt?.split("T")[0] || "",
          time: item.lastUpdatedAt?.split("T")[1]?.slice(0, 8) || "",
        };
      });

      // ✅ MERGE — don’t overwrite live socket updates
//      setStatusByEnv((prev) => ({
//   ...prev,
//   [envConfig.env]: {
//     ...(prev[envConfig.env] || {}),
//     ...hydrated,
//   },
// }));
setStatusByEnv((prev) => ({
  ...prev,
  [envConfig.env]: hydrated,
}));
    } catch (err) {
      console.error("❌ Failed to load DynamoDB data", err);
    }
  };

  /* ================= WEBSOCKET ================= */
  useEffect(() => {
    if (!envConfig) return;

    // 1️⃣ Hydrate first
    loadFromDynamoDB();

    // 2️⃣ Close previous socket
    if (wsRef.current) {
      wsRef.current.close();
    }

    const ws = new WebSocket(envConfig.ws);
    wsRef.current = ws;

    /* ---------- CONNECT ---------- */
    ws.onopen = () => {
      setIsConnected(true);

      // 🔥 Rehydrate on reconnect
      loadFromDynamoDB();
    };

    /* ---------- MESSAGE ---------- */
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (!data?.lambda) return;

      // 🚨 ENV GUARD
      if (data.env !== envConfig.env) return;

      setStatusByEnv((prev) => ({
        ...prev,
        [envConfig.env]: {
          ...(prev[envConfig.env] || createDefaultLambdaStatus()),
          [data.lambda]: {
            status: data.color === "red" ? "🔴" : "🟢",
            date: new Date().toISOString().split("T")[0],
            time: new Date().toISOString().split("T")[1].slice(0, 8),
          },
        },
      }));
    };

    /* ---------- DISCONNECT ---------- */
    ws.onclose = () => setIsConnected(false);
    ws.onerror = () => setIsConnected(false);

    return () => ws.close();
  }, [currentPath]);

  /* ================= POLLING FALLBACK ================= */
  useEffect(() => {
    if (!envConfig) return;

    const interval = setInterval(() => {
      loadFromDynamoDB();
    }, 60000); // every 60 sec

    return () => clearInterval(interval);
  }, [envConfig]);

  /* ================= ACTIVE ENV DATA ================= */
  const status = useMemo(
    () => statusByEnv[envConfig?.env] || createDefaultLambdaStatus(),
    [statusByEnv, envConfig]
  );

  /* ================= SORT ================= */
  const sortedLambdas = useMemo(() => {
    return LAMBDAS.filter((fn) =>
      fn.toLowerCase().includes(searchTerm.toLowerCase())
    ).sort((a, b) => {
      const aErr = status[a]?.status === "🔴";
      const bErr = status[b]?.status === "🔴";
      return aErr === bErr ? a.localeCompare(b) : aErr ? -1 : 1;
    });
  }, [searchTerm, status]);

  /* ================= GRAPH ================= */
  // const { total, healthyCount, errorCount, okPercent } = useMemo(() => {
  //   const total = LAMBDAS.length;

  //   const errorCount = Object.values(status).filter(
  //     (v) => v.status === "🔴"
  //   ).length;

  //   return {
  //     total,
  //     healthyCount: total - errorCount,
  //     errorCount,
  //     okPercent: total ? ((total - errorCount) / total) * 100 : 0,
  //   };
  // }, [status]);

const { total, healthyCount, errorCount, okPercent } = useMemo(() => {
  const monitoredLambdas = sortedLambdas.map((lambda) => status[lambda]);

  const total = monitoredLambdas.length;

  const errorCount = monitoredLambdas.filter(
    (item) => item?.status === "🔴"
  ).length;

  const healthyCount = monitoredLambdas.filter(
    (item) => item?.status === "🟢"
  ).length;

  console.log("Total:", total);
  console.log("Healthy:", healthyCount);
  console.log("Failed:", errorCount);

  return {
    total,
    healthyCount,
    errorCount,
    okPercent: total ? (healthyCount / total) * 100 : 0,
  };
}, [status, sortedLambdas]);

  /* ================= UI ================= */
  return (
    <div className={`app ${isDark ? "dark" : "light"}`}>
      {/* ---------- SIDEBAR ---------- */}
      <aside className="sidebar">
        <SiderbarHeading isConnected={isConnected} />

        <nav className="sidebar-nav">
        
            <>
              <ListItem
                Title="Development"
                Icon={<MdOutlineMonitorHeart />}
                to="/dev-env"
              />
              <ListItem
                Title="Demo Environment"
                Icon={<MdOutlineMonitorHeart />}
                to="/demo-env"
              />
               <ListItem
                Title="Test Environment"
                Icon={<MdOutlineMonitorHeart />}
                to="/test-env"
              />
            </>
            
            
      
            
            {/* <ListItem
              Title="SAN-Development"
              Icon={<MdOutlineMonitorHeart />}
              to="/san-env"
            />
              <ListItem
                Title="SAN-Production"
                Icon={<MdOutlineMonitorHeart />}
                to="/san-prod-env"
              />
         <ListItem
            Title="COBL Environment"
            Icon={<MdOutlineMonitorHeart />}
            to="/cobl-env"
          />

          <ListItem
            Title="HC internal Environment"
            Icon={<MdOutlineMonitorHeart />}
            to="/hcinternal-env"
          /> */}
        </nav>

        <SidebarFooter isConnected={isConnected} />
      </aside>

      {/* ---------- MAIN ---------- */}
      <main className="main">
        <Header isDark={isDark} setIsDark={setIsDark} />

        <div className="content">
          <LeftGraph
            okPercent={okPercent}
            healthyCount={healthyCount}
            errorCount={errorCount}
            total={total}
          />

          <section className="card table-card">
            <LambdaTable
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              sortedLambdas={sortedLambdas}
              status={status}
            />
          </section>
        </div>
      </main>
    </div>
  );
};

export default LambdaDashBoard;